import type { PermissionDef, PermissionSection } from '@clockwise/shared';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { type ReactNode, useId, useState } from 'react';
import { Button } from '@/components/ui/button';

interface Props {
  /** The registry laid out by section (`sectionRegistry`). */
  sections: PermissionSection[];
  /** One permission's row — an `<li>`. */
  renderRow: (def: PermissionDef) => ReactNode;
  /** Shown beside a section's title, e.g. "3 of 15" granted. */
  count?: (section: PermissionSection) => string;
  /** Sections that must stay open whatever the toggle says — e.g. one with a
   *  validation error, which a collapsed section would hide (G12). */
  keepOpen?: (section: PermissionSection) => boolean;
}

/**
 * Permissions laid out by section (People, Customers, Time, …), each a
 * collapsible list: a section's groups are small labels over their rows,
 * with no box around each group. Every section starts expanded; a single
 * control collapses or expands them all. Shared by the role editor and the
 * role page so both read the same way.
 */
export function PermissionSectionList({ sections, renderRow, count, keepOpen }: Props) {
  const idPrefix = useId();
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set());
  const allCollapsed = sections.length > 0 && sections.every((s) => collapsed.has(s.title));

  const toggle = (title: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(title)) next.delete(title);
      else next.add(title);
      return next;
    });

  return (
    <div className="space-y-2">
      <div className="flex justify-end">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 text-xs"
          onClick={() =>
            setCollapsed(allCollapsed ? new Set() : new Set(sections.map((s) => s.title)))
          }
        >
          {allCollapsed ? 'Expand all' : 'Collapse all'}
        </Button>
      </div>
      {sections.map((section) => {
        const open = !collapsed.has(section.title) || (keepOpen?.(section) ?? false);
        const bodyId = `${idPrefix}-${section.title}`;
        return (
          <section key={section.title} aria-label={section.title} className="border-b pb-2">
            <div className="flex items-center gap-2">
              <h3 className="flex-1">
                <button
                  type="button"
                  aria-expanded={open}
                  aria-controls={bodyId}
                  onClick={() => toggle(section.title)}
                  className="flex w-full items-center gap-1.5 rounded-md py-1.5 text-left text-sm font-semibold text-foreground hover:bg-muted/50"
                >
                  {open ? (
                    <ChevronDown className="size-4 shrink-0" aria-hidden="true" />
                  ) : (
                    <ChevronRight className="size-4 shrink-0" aria-hidden="true" />
                  )}
                  {section.title}
                </button>
              </h3>
              {count ? (
                <span className="text-xs text-muted-foreground">{count(section)}</span>
              ) : null}
            </div>
            {open ? (
              <div id={bodyId} className="space-y-1 pl-6">
                {section.groups.map(([group, defs]) => (
                  <div key={group}>
                    <h4 className="pt-1.5 text-xs font-medium text-muted-foreground">{group}</h4>
                    <ul className="divide-y">{defs.map(renderRow)}</ul>
                  </div>
                ))}
              </div>
            ) : null}
          </section>
        );
      })}
    </div>
  );
}
